import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Tag, Button, Empty, Spin } from 'antd';
import { ArrowLeft, FolderOpen, FileText } from 'lucide-react';
import { PageHeader, Card } from '@/components/common/PageHeader';
import { documentService } from '@/services/documentService';

export function DocumentCategoriesPage() {
  const navigate = useNavigate();

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['doc-categories'],
    queryFn: documentService.listCategories,
  });

  const totalDocs = categories.reduce((sum, c) => sum + (c.documentCount ?? 0), 0);

  return (
    <>
      <PageHeader
        title="Danh mục tài liệu"
        subtitle={`Bấm vào 1 loại để xem tài liệu thuộc loại đó · Tổng ${totalDocs} tài liệu`}
        extra={
          <Button icon={<ArrowLeft size={14} />} onClick={() => navigate('/documents')}>
            Về danh sách tài liệu
          </Button>
        }
      />

      {isLoading ? (
        <Card><div style={{ textAlign: 'center', padding: 60 }}><Spin /></div></Card>
      ) : categories.length === 0 ? (
        <Card><Empty description="Chưa có loại tài liệu nào" /></Card>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 12,
        }}>
          {categories.map((c) => {
            const count = c.documentCount ?? 0;
            const color = c.color ?? '#6699CC';
            const isEmpty = count === 0;
            return (
              <div
                key={c.id}
                onClick={() => navigate(`/documents?categoryId=${c.id}`)}
                style={{
                  background: '#fff',
                  border: '1px solid #e1e5eb',
                  borderRadius: 8,
                  padding: 16,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = color;
                  e.currentTarget.style.boxShadow = `0 2px 8px ${color}33`;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e1e5eb';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Color stripe */}
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: 4, background: color,
                }} />

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Tag color={c.color || 'default'}
                    style={{ fontFamily: 'monospace', fontWeight: 600, marginRight: 0 }}>
                    {c.code}
                  </Tag>
                  {!c.isActive && <Tag color="default" style={{ fontSize: 10 }}>Tắt</Tag>}
                </div>

                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: '#1a1d24' }}>
                  {c.name}
                </div>

                {c.description && (
                  <div style={{
                    fontSize: 11, color: '#7d8a98', marginBottom: 12,
                    minHeight: 30, lineHeight: 1.4,
                    overflow: 'hidden', display: '-webkit-box',
                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }}>
                    {c.description}
                  </div>
                )}

                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  paddingTop: 10, borderTop: '1px solid #f0f2f5', marginTop: c.description ? 0 : 12,
                }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    color: isEmpty ? '#a5b0c0' : color, fontWeight: 600, fontSize: 15,
                  }}>
                    <FileText size={14} />
                    {count}
                    <span style={{ fontSize: 11, fontWeight: 400, color: '#7d8a98' }}>
                      tài liệu
                    </span>
                  </div>
                  <FolderOpen size={14} style={{ color: '#a5b0c0' }} />
                </div>

                <div style={{
                  fontSize: 10, color: '#a5b0c0', marginTop: 8, fontFamily: 'monospace',
                }}>
                  Mã: {c.prefix}-YYYY-MM-###
                  {c.retentionMonths && (
                    <span style={{ marginLeft: 8 }}>
                      · Lưu {c.retentionMonths >= 12 ? `${Math.floor(c.retentionMonths / 12)} năm` : `${c.retentionMonths}th`}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <Card>
          <div style={{ fontSize: 12, color: '#7d8a98', lineHeight: 1.7 }}>
            <b style={{ color: '#1a1d24' }}>💡 Quy tắc sinh mã tài liệu:</b><br />
            Mã tài liệu tự động được sinh theo format: <code>{'<Tiền tố>-YYYY-MM-###'}</code><br />
            Ví dụ: HD-2026-04-001 = Hợp đồng, tháng 4/2026, số thứ tự 001<br />
            Khi thêm mới tài liệu, hệ thống tự kiểm tra số thứ tự cao nhất đang có trong tháng và cấp mã kế tiếp.
          </div>
        </Card>
      </div>
    </>
  );
}
